import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageSquare, X, Check, Star, MapPin, Bug } from "lucide-react";

/** Map a route path to a friendly page label for the feedback prefill. */
function describePage(path) {
  if (!path) return "Unknown page";
  const trimmed = path.replace(/\/+$/, "");
  if (trimmed === "" || trimmed === "/") return "Dashboard";
  const map = {
    "/welcome":         "Welcome page",
    "/login":           "Login",
    "/register":        "Register",
    "/feed-resume":     "Feed Resmo · Resume",
    "/feed-job":        "Feed Resmo · Job Description",
    "/skills":          "Skills Lab",
    "/network":         "Networking (Phase 3)",
    "/studio":          "Resume Studio",
    "/history":         "History",
    "/stepping-stones": "Stepping Stones (Reflection)",
    "/billing":         "Billing & Plans",
    "/admin/activity":  "Admin · Activity",
    "/admin/feedback":  "Admin · Feedback",
    "/admin/outcomes":  "Admin · Outcomes",
  };
  if (map[trimmed]) return map[trimmed];
  // Dynamic routes: /dashboard/:id, /strategy-brief/:id, etc.
  if (trimmed.startsWith("/dashboard/"))      return "Tailored resume result";
  if (trimmed.startsWith("/strategy-brief"))  return "Strategy Brief";
  if (trimmed.startsWith("/skills/"))         return "Skills Lab · Assessment result";
  return trimmed;
}

export default function FeedbackButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("feedback");
  const [msg, setMsg] = useState("");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [pageLabel, setPageLabel] = useState("");
  const [pagePath, setPagePath] = useState("");
  const isBug = kind === "bug";

  // Pre-populate context every time the modal opens so the user (and the
  // backend triage) immediately know which page the feedback came from.
  useEffect(() => {
    if (!open) return;
    const path = window.location.pathname;
    const label = describePage(path);
    setPageLabel(label);
    setPagePath(path);
    if (!msg) {
      setMsg(`From: ${label} (${path})\n\n`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (msg.trim().length < 4) {
      setError("Tell me a bit more — at least a few words.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/feedback", {
        message: msg.trim(),
        rating: isBug ? null : (rating || null),
        page: pagePath || window.location.pathname,
        page_label: pageLabel,
        kind,
      });
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setMsg("");
        setRating(0);
      }, 1800);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {user && (
        <div className="fixed bottom-5 left-5 z-[60] flex items-center gap-2">
          <button
            onClick={() => { setKind("feedback"); setOpen(true); }}
            className="rm-btn rm-btn-primary px-3 py-2 text-xs"
            data-testid="feedback-fab"
            title="Send feedback"
            style={{ boxShadow: "4px 4px 0 0 #0F1B2D" }}
          >
            <MessageSquare size={14} />
            <span className="hidden sm:inline">Feedback</span>
          </button>
          <button
            onClick={() => { setKind("bug"); setOpen(true); }}
            className="rm-btn px-3 py-2 text-xs"
            data-testid="report-bug-fab"
            title="Report a bug"
            style={{ background: "#B91C1C", borderColor: "#0F1B2D", color: "white", boxShadow: "4px 4px 0 0 #0F1B2D" }}
          >
            <Bug size={14} />
            <span className="hidden sm:inline">Report a bug</span>
          </button>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(15,27,45,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => !submitting && setOpen(false)}
          data-testid="feedback-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rm-card-static max-w-md w-full p-6"
            style={{
              background: "white",
              borderColor: "#0F1B2D",
              boxShadow: `6px 6px 0 0 ${isBug ? "#B91C1C" : "#FF6A00"}`,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                {isBug
                  ? <Bug size={20} style={{ color: "#B91C1C" }} />
                  : <MessageSquare size={20} className="rm-text-orange" />}
                <div className="font-display uppercase text-xl" style={{ color: "#0F1B2D" }}>
                  {isBug ? "Report a bug" : "Tell us what's up"}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="feedback-close">
                <X size={18} />
              </button>
            </div>

            {sent ? (
              <div className="py-6 text-center" data-testid="feedback-sent">
                <Check size={36} className="mx-auto mb-2" style={{ color: "#1A7F5C" }} />
                <div className="font-display uppercase text-lg" style={{ color: "#0F1B2D" }}>
                  {isBug ? "Bug logged — we're on it." : "Got it, thank you."}
                </div>
                <div className="text-sm mt-1" style={{ color: "#475569" }}>
                  {isBug ? "Thank you for making Resmo sturdier." : "Every note shapes Resmo."}
                </div>
              </div>
            ) : (
              <>
                {pageLabel && (
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3"
                    style={{
                      background: "#FFF2EC", border: "2px solid #FF6A00", borderRadius: 999,
                      fontSize: 10.5, fontWeight: 700, color: "#B45309",
                      letterSpacing: "0.08em", textTransform: "uppercase",
                    }}
                    data-testid="feedback-page-chip"
                    title={`Feedback will be tagged with the page path: ${pagePath}`}
                  >
                    <MapPin size={11} />
                    {pageLabel}
                  </div>
                )}
                {!isBug && (
                <div className="mb-3">
                  <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1.5" style={{ color: "#475569" }}>
                    How's it going? (optional)
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n === rating ? 0 : n)}
                        className="p-1"
                        data-testid={`feedback-rating-${n}`}
                      >
                        <Star
                          size={20}
                          fill={n <= rating ? "#FFC145" : "transparent"}
                          stroke={n <= rating ? "#FF6A00" : "#94A3B8"}
                          strokeWidth={2}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                )}

                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder={isBug
                    ? "What happened? What did you expect instead? Steps to reproduce help a ton."
                    : "Bugs, feature requests, love notes — anything goes."}
                  rows={4}
                  className="w-full text-sm p-2.5 mb-2"
                  style={{
                    background: "#FFF8E7",
                    color: "#0F1B2D",
                    border: "2px solid #0F1B2D",
                    fontFamily: "'Poppins', system-ui, sans-serif",
                    resize: "vertical",
                  }}
                  data-testid="feedback-textarea"
                  autoFocus
                />

                {error && (
                  <div className="text-xs mb-2" style={{ color: "#DC2626", fontWeight: 600 }} data-testid="feedback-error">
                    {error}
                  </div>
                )}

                <button
                  onClick={submit}
                  disabled={submitting}
                  className={`w-full rm-btn ${isBug ? "" : "rm-btn-primary"} justify-center py-2.5 disabled:opacity-60`}
                  style={isBug ? { background: "#B91C1C", borderColor: "#0F1B2D", color: "white" } : undefined}
                  data-testid="feedback-submit"
                >
                  {submitting ? "Sending…" : isBug ? "Send bug report →" : "Send feedback →"}
                </button>

                <div className="mt-3 text-[10px] text-center font-mono-rm uppercase tracking-widest" style={{ color: "#94A3B8" }}>
                  Goes straight to the team.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
